import { applySafeArea } from '../../stores/common';
import { initUserClientType, userState } from '../../stores/user';
import { initRNBridge, postToApp, registerRNHandler, type RNMessage } from '../../lib/rnBridge';

export const ACTIVITY_BOOTSTRAP_TYPE = 'APP_TO_WEB_ActivityBootstrap';
export const ACTIVITY_BOOTSTRAP_REQUEST_TYPE = 'WEB_TO_APP_ActivityBootstrap_Request';
export const ACTIVITY_TOKEN_EXPIRED_TYPE = 'WEB_TO_APP_ActivityTokenExpired';

export type ActivityBootstrapPayload = {
  authorization?: string | null;
  userId?: string | number | null;
  applyStatus?: string | null;
  lang?: string | null;
  scheme?: 'light' | 'dark' | string | null;
  riseFallMode?: string | null;
  safeArea?: {
    top?: number;
    bottom?: number;
  } | null;
};

export type ActivityBootstrapMessage = RNMessage & {
  type: typeof ACTIVITY_BOOTSTRAP_TYPE;
  requestId?: string;
  payload?: ActivityBootstrapPayload;
};

export type ActivitySession = {
  requestId: string | null;
  authorization: string | null;
  userId: string;
  applyStatus: string;
  lang: string;
  scheme: 'light' | 'dark';
  riseFallMode: string;
  safeArea: {
    top: number;
    bottom: number;
  };
};

export const ACTIVITY_BOOTSTRAP_TIMEOUT_MS = 3000;

export class ActivityBootstrapTimeoutError extends Error {
  readonly code = 'ACTIVITY_BOOTSTRAP_TIMEOUT';

  constructor() {
    super('Activity WebView bootstrap timeout');
    this.name = 'ActivityBootstrapTimeoutError';
  }
}

export class ActivityAppRequiredError extends Error {
  readonly code = 'ACTIVITY_APP_REQUIRED';

  constructor() {
    super('Activity page must be opened in the App WebView');
    this.name = 'ActivityAppRequiredError';
  }
}

export class ActivityAuthRequiredError extends Error {
  readonly code = 'ACTIVITY_AUTH_REQUIRED';

  constructor() {
    super('Activity page requires an authenticated App session');
    this.name = 'ActivityAuthRequiredError';
  }
}

export type ActivityTokenExpiredReason = 'http-401' | 'biz-401' | 'missing-session';

let activitySessionPromise: Promise<ActivitySession> | null = null;
let activitySession: ActivitySession | null = null;
let unregisterBootstrapHandler: (() => void) | null = null;
let activityTokenExpiredNotified = false;

export function isAppWebView(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).ReactNativeWebView);
}

export function isActivityDocument(): boolean {
  if (typeof window === 'undefined') return false;
  return /\/activity(?:\/|$)/i.test(window.location.pathname);
}

export function isActivityAppWebView(): boolean {
  return isActivityDocument() && isAppWebView();
}

function normalizeText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function applyTheme(scheme: string | null | undefined): void {
  if (scheme !== 'light' && scheme !== 'dark') return;
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(scheme);
  document.body.classList.remove('light', 'dark');
  document.body.classList.add(scheme);
}

function normalizeSession(message: ActivityBootstrapMessage): ActivitySession {
  const payload = message.payload ?? {};
  const safeArea = payload.safeArea ?? {};
  return {
    requestId: normalizeText(message.requestId) || null,
    authorization: normalizeText(payload.authorization) || null,
    userId: normalizeText(payload.userId),
    applyStatus: normalizeText(payload.applyStatus),
    lang: normalizeText(payload.lang) || 'zh-CN',
    scheme: payload.scheme === 'dark' ? 'dark' : 'light',
    riseFallMode: normalizeText(payload.riseFallMode),
    safeArea: {
      top: typeof safeArea.top === 'number' && Number.isFinite(safeArea.top) ? Math.max(0, safeArea.top) : 0,
      bottom:
        typeof safeArea.bottom === 'number' && Number.isFinite(safeArea.bottom) ? Math.max(0, safeArea.bottom) : 0,
    },
  };
}

function applySession(message: ActivityBootstrapMessage): ActivitySession {
  const next = normalizeSession(message);
  activitySession = next;

  userState.isLogin = Boolean(next.authorization);
  userState.id = next.userId;
  userState.applyStatus = next.applyStatus;
  applyTheme(next.scheme);
  applySafeArea({
    bottom: next.safeArea.bottom,
    top: next.safeArea.top,
    insets: {
      bottom: next.safeArea.bottom,
      top: next.safeArea.top,
      left: 0,
      right: 0,
    },
  });

  return next;
}

/**
 * Activity H5 的单一启动门闩。
 *
 * 仅 App WebView 可启动；H5 主动请求当前 document 的 Bootstrap。
 */
export function bootActivitySession(): Promise<ActivitySession> {
  if (activitySessionPromise) return activitySessionPromise;
  if (!isAppWebView()) return Promise.reject(new ActivityAppRequiredError());

  initUserClientType();
  initRNBridge();

  activitySessionPromise = new Promise<ActivitySession>((resolve, reject) => {
    const requestId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `activity-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    let settled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const finish = (session: ActivitySession) => {
      if (settled) return;
      settled = true;
      if (timeout !== undefined) clearTimeout(timeout);
      unregisterBootstrapHandler?.();
      unregisterBootstrapHandler = null;
      resolve(session);
    };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      if (timeout !== undefined) clearTimeout(timeout);
      activitySessionPromise = null;
      unregisterBootstrapHandler?.();
      unregisterBootstrapHandler = null;
      reject(error);
    };

    unregisterBootstrapHandler = registerRNHandler((message) => {
      if (message.type !== ACTIVITY_BOOTSTRAP_TYPE || message.requestId !== requestId) return;
      finish(applySession(message as ActivityBootstrapMessage));
    });

    postToApp({
      type: ACTIVITY_BOOTSTRAP_REQUEST_TYPE,
      requestId,
      timestamp: Date.now(),
    });
    timeout = setTimeout(() => {
      fail(new ActivityBootstrapTimeoutError());
    }, ACTIVITY_BOOTSTRAP_TIMEOUT_MS);
  });

  return activitySessionPromise;
}

export function getActivitySession(): ActivitySession | null {
  return activitySession;
}

/**
 * `http-interceptor` 使用的请求级鉴权来源。
 * 非活动文档返回 undefined，让主站 H5 继续使用旧登录态。
 * 活动 App WebView：Bootstrap 下发的 token（到达前 null，禁止误用 localStorage）。
 * 外部浏览器活动页：读主站 `localStorage.token`（与 H5 登录同源）。
 */
export function getActivityAuthorization(): string | null | undefined {
  if (!isActivityDocument()) return undefined;
  if (isAppWebView()) return activitySession?.authorization ?? null;
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/** 活动 API 鉴权失效：App 通知 Native；浏览器清 H5 token。 */
export function notifyActivityTokenExpired(reason: ActivityTokenExpiredReason): void {
  if (!isActivityDocument()) return;

  if (isActivityAppWebView()) {
    if (activityTokenExpiredNotified) return;
    activityTokenExpiredNotified = true;
    if (activitySession) {
      activitySession = {
        ...activitySession,
        authorization: null,
        userId: '',
        applyStatus: '',
      };
    }
    userState.isLogin = false;
    userState.id = '';
    userState.applyStatus = '';
    postToApp({
      type: ACTIVITY_TOKEN_EXPIRED_TYPE,
      requestId: activitySession?.requestId ?? undefined,
      payload: { reason },
      timestamp: Date.now(),
    });
    return;
  }

  // 普通浏览器：清主站 token，后续报名门禁引导重新登录
  try {
    localStorage.removeItem('token');
  } catch {
    /* ignore */
  }
  userState.isLogin = false;
  userState.id = '';
  userState.applyStatus = '';
}

export function isActivityTokenExpiredNotified(): boolean {
  return activityTokenExpiredNotified;
}
